<template>
	<div class="content-wrapper">
		<div class="container flex-grow-1 container-p-y">

			<div class="d-flex flex-column justify-content-center mb-6">
				<h4 class="mb-1">{{ $t('companySettings.pageTitle') }}</h4>
				<p class="mb-0">{{ $t('companySettings.pageDescription') }}</p>
			</div>

			<!-- Société -->
			<div class="card mb-6">
				<h5 class="card-header">{{ $t('companySettings.companyTitle') }}</h5>
				<div class="card-body pt-1" v-if="companyForm">
					<p class="text-muted small mb-5">{{ $t('companySettings.companyDescription') }}</p>

					<form @submit.prevent="saveCompany">
						<div class="row g-5 mb-6">
							<div class="col-md-12">
								<div class="form-floating form-floating-outline">
									<input
										type="text"
										class="form-control"
										id="companyName"
										v-model="companyForm.companyName" />
									<label for="companyName">{{ $t('common.fields.companyName') }}</label>
									<small v-if="companyErrors.companyName" class="text-danger">{{ companyErrors.companyName }}</small>
								</div>
							</div>

							<div class="col-md-12">
								<div class="form-floating form-floating-outline">
									<input
										type="text"
										class="form-control"
										id="companyAddress"
										v-model="companyForm.address" />
									<label for="companyAddress">{{ $t('common.fields.address') }}</label>
									<small v-if="companyErrors.address" class="text-danger">{{ companyErrors.address }}</small>
								</div>
							</div>

							<div class="col-md-6">
								<div class="form-floating form-floating-outline">
									<input
										type="text"
										class="form-control"
										id="companyNumber"
										maxlength="9"
										v-model="companyForm.companyNumber" />
									<label for="companyNumber">{{ $t('common.fields.siren') }}</label>
									<small v-if="companyErrors.companyNumber" class="text-danger">{{ companyErrors.companyNumber }}</small>
								</div>
							</div>

							<div class="col-md-6">
								<div class="form-floating form-floating-outline">
									<input
										type="text"
										class="form-control"
										id="vatNumber"
										v-model="companyForm.vatNumber" />
									<label for="vatNumber">{{ $t('common.fields.vatNumber') }}</label>
								</div>
							</div>
						</div>

						<button type="submit" class="btn btn-primary" :disabled="companySaving">
							{{ companySaving ? $t('common.actions.saving') : $t('common.actions.save') }}
						</button>
						<span v-if="companySaved" class="text-success ms-3 small">{{ $t('companySettings.savedConfirmation') }}</span>
					</form>
				</div>
			</div>

			<!-- Préférences -->
			<div class="card mb-6">
				<h5 class="card-header">{{ $t('companySettings.preferencesTitle') }}</h5>
				<div class="card-body pt-1" v-if="prefsForm">
					<p class="text-muted small mb-5">{{ $t('companySettings.preferencesDescription') }}</p>

					<div class="form-check form-switch mb-4">
						<input class="form-check-input" type="checkbox" id="prefSendTrainee" v-model="prefsForm.sendCertificateToTrainee" />
						<label class="form-check-label" for="prefSendTrainee">
							<strong>{{ $t('companySettings.prefs.sendCertificateToTrainee') }}</strong>
							<div class="text-muted small">{{ $t('companySettings.prefs.sendCertificateToTraineeHint') }}</div>
						</label>
					</div>

					<div class="form-check form-switch mb-4">
						<input class="form-check-input" type="checkbox" id="prefSendManagers" v-model="prefsForm.sendCertificatesToAllManagers" />
						<label class="form-check-label" for="prefSendManagers">
							<strong>{{ $t('companySettings.prefs.sendCertificatesToAllManagers') }}</strong>
							<div class="text-muted small">{{ $t('companySettings.prefs.sendCertificatesToAllManagersHint') }}</div>
						</label>
					</div>

					<div class="form-check form-switch mb-4" v-if="displayConsultants">
						<input class="form-check-input" type="checkbox" id="prefSendConsultants" v-model="prefsForm.sendCertificatesToAllConsultantsOfAgency" />
						<label class="form-check-label" for="prefSendConsultants">
							<strong>{{ $t('companySettings.prefs.sendCertificatesToAllConsultantsOfAgency') }}</strong>
							<div class="text-muted small">{{ $t('companySettings.prefs.sendCertificatesToAllConsultantsOfAgencyHint') }}</div>
						</label>
					</div>

					<button type="button" class="btn btn-primary" :disabled="prefsSaving" @click="savePreferences">
						{{ prefsSaving ? $t('common.actions.saving') : $t('common.actions.save') }}
					</button>
					<span v-if="prefsSaved" class="text-success ms-3 small">{{ $t('companySettings.savedConfirmation') }}</span>
				</div>
			</div>
		</div>
		<div class="content-backdrop fade"></div>
	</div>
</template>

<script>
import HTTP from "@/axios";

export default {
	name: "CompanySettingsPage",

	data() {
		return {
			companyForm: null,
			companyErrors: {},
			companySaving: false,
			companySaved: false,
			prefsForm: null,
			prefsSaving: false,
			prefsSaved: false,
			displayConsultants: false,
		};
	},

	methods: {
		async fetchFeaturesFlags() {
			try {
				const response = await HTTP.get("/company/flags");
				this.displayConsultants = response.data.displayConsultants;
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');
				console.error("Erreur API:", errorMessage);
			}
		},

		async fetchCompany() {
			try {
				const response = await HTTP("/company");
				const company = response.data.company;

				this.companyForm = {
					companyName: company.name || "",
					address: company.address || "",
					companyNumber: company.companyNumber || "",
					vatNumber: company.vatNumber || ""
				};

				this.prefsForm = {
					sendCertificateToTrainee: !!company.metadata?.sendCertificateToTrainee,
					sendCertificatesToAllManagers: !!company.metadata?.sendCertificatesToAllManagers,
					sendCertificatesToAllConsultantsOfAgency: !!company.metadata?.sendCertificatesToAllConsultantsOfAgency
				};
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		validateCompanyForm() {
			this.companyErrors = {};
			if (!this.companyForm.companyName?.trim())
				this.companyErrors.companyName = this.$t('validation.requiredCompanyName');
			if (!this.companyForm.address?.trim())
				this.companyErrors.address = this.$t('validation.requiredAddress');
			if (!this.companyForm.companyNumber?.trim() || !/^\d{9}$/.test(this.companyForm.companyNumber.trim()))
				this.companyErrors.companyNumber = this.$t('validation.invalidSiren');
			return Object.keys(this.companyErrors).length === 0;
		},

		buildCompanyPayload(extra = {}) {
			return {
				companyName: this.companyForm.companyName.trim(),
				address: this.companyForm.address.trim(),
				companyNumber: this.companyForm.companyNumber.trim(),
				vatNumber: this.companyForm.vatNumber?.trim() || null,
				...extra
			};
		},

		async saveCompany() {
			if (!this.validateCompanyForm())
				return;

			this.companySaving = true;
			this.companySaved = false;

			try {
				await HTTP.put("/company", this.buildCompanyPayload());

				this.companySaved = true;
				setTimeout(() => { this.companySaved = false; }, 2500);
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			} finally {
				this.companySaving = false;
			}
		},

		async savePreferences() {
			if (!this.validateCompanyForm())
				return;

			this.prefsSaving = true;
			this.prefsSaved = false;

			try {
				await HTTP.put("/company", this.buildCompanyPayload({
					sendCertificateToTrainee: this.prefsForm.sendCertificateToTrainee,
					sendCertificatesToAllManagers: this.prefsForm.sendCertificatesToAllManagers,
					sendCertificatesToAllConsultantsOfAgency: this.prefsForm.sendCertificatesToAllConsultantsOfAgency
				}));

				this.prefsSaved = true;
				setTimeout(() => { this.prefsSaved = false; }, 2500);
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			} finally {
				this.prefsSaving = false;
			}
		}
	},

	async mounted() {
		await this.fetchCompany();
		await this.fetchFeaturesFlags();
	}
};
</script>
